import React from 'react';
import { Table, Badge, Text, Stack } from '@mantine/core';
import { getAgeDivision } from '../../services/schoolService';
import ReportLink from '../Reports/ReportLink';

/**
 * Compact high-level roster view for a school's Profile page.
 *
 * Expects each student in `students` to have dancer_information and variations
 * embedded (via a nested Supabase select in the caller).
 *
 * `year` is the season being shown. Age divisions are computed against Jan 1 of
 * the competition year, so a past roster must pass its own year or every dancer
 * silently shifts up a division.
 */
const SchoolStudentsSummaryTable = ({ students = [], year }) => {
  if (!students || students.length === 0) {
    return (
      <Text size="sm" color="dimmed" style={{ textAlign: 'center', padding: '2rem 0' }}>
        No students registered yet.
      </Text>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table
        highlightOnHover
        verticalSpacing="sm"
        horizontalSpacing="sm"
        striped
        style={{ fontSize: '0.875rem' }}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Student</Table.Th>
            <Table.Th>Division</Table.Th>
            <Table.Th>Variation(s)</Table.Th>
            <Table.Th>Report</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {students.map((s) => {
            const di = s.dancer_information || {};
            const vars = Array.isArray(s.variations) ? s.variations : [];
            const name = `${di.first_name || ''} ${di.last_name || ''}`.trim() || 'Unnamed';
            const ageDivision = di.date_of_birth ? getAgeDivision(di.date_of_birth, year) : null;
            const sortedVars = vars
              .slice()
              .sort((a, b) => (a.variation_number || 0) - (b.variation_number || 0));

            return (
              <Table.Tr key={s.id}>
                <Table.Td>
                  <Text size="sm" weight={600}>{name}</Text>
                </Table.Td>

                <Table.Td>
                  {ageDivision
                    ? <Badge size="sm" color="yellow" variant="light">{ageDivision}</Badge>
                    : <Text size="xs" color="dimmed">—</Text>}
                </Table.Td>

                {/* One line per variation in both columns, so a student with
                    two variations gets two report buttons (one per variation),
                    while still occupying a single row. */}
                <Table.Td>
                  {sortedVars.length === 0 ? (
                    <Text size="xs" c="dimmed">None</Text>
                  ) : (
                    <Stack gap={6}>
                      {sortedVars.map(v => (
                        <Text key={v.id || v.variation_number} size="sm" style={{ lineHeight: '26px' }}>
                          {v.variation_name || `Variation ${v.variation_number}`}
                        </Text>
                      ))}
                    </Stack>
                  )}
                </Table.Td>

                <Table.Td>
                  {sortedVars.length === 0 ? (
                    <ReportLink registration={s} />
                  ) : (
                    <Stack gap={6}>
                      {sortedVars.map(v => (
                        <div key={v.id || v.variation_number}>
                          <ReportLink registration={s} variationId={v.id} />
                        </div>
                      ))}
                    </Stack>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </div>
  );
};

export default SchoolStudentsSummaryTable;
